import { useState, useEffect, useCallback } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { Flash, Modal, FormField, ModalActions, Pagination } from "../components/AdminUI";
import "../styles/admin.css";

export default function DepotManager() {
  const [depots, setDepots]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash]     = useState({ message: "", type: "success" });

  // フィルター
  const [searchQ, setSearchQ]         = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 20;

  // 追加・編集モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [editDepot, setEditDepot] = useState(null);
  const [form, setForm] = useState({ name: "", short_name: "", code_prefix: "", sort_id: "" });
  const [saving, setSaving] = useState(false);

  // ── データ取得 ──
  const loadDepots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/depots");
      setDepots(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "営業所の取得に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepots();
  }, [loadDepots]);

  function showFlash(message, type = "success") {
    setFlash({ message, type });
  }

  // ── フィルタリング・ページネーション ──
  const filtered = depots.filter((d) => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (
      (d.name ?? "").toLowerCase().includes(q) ||
      (d.short_name ?? "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage   = Math.min(currentPage, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  function handleSearchChange(v) { setSearchQ(v); setCurrentPage(1); }

  // ── モーダル操作 ──
  function openAdd() {
    setEditDepot(null);
    setForm({ name: "", short_name: "", code_prefix: "", sort_id: "" });
    setModalOpen(true);
  }

  function openEdit(depot) {
    setEditDepot(depot);
    setForm({
      name:       depot.name ?? "",
      short_name: depot.short_name ?? "",
      code_prefix: depot.code_prefix ?? "",
      sort_id:    depot.sort_id != null ? String(depot.sort_id) : "",
    });
    setModalOpen(true);
  }

  function closeModal() { setModalOpen(false); setEditDepot(null); }

  async function handleSave() {
    if (!form.name.trim())       { showFlash("営業所名は必須です", "error"); return; }
    if (!form.short_name.trim()) { showFlash("略称は必須です", "error"); return; }
    if (!form.sort_id.trim())    { showFlash("表示順は必須です", "error"); return; }

    setSaving(true);
    const payload = {
      name:       form.name.trim(),
      short_name: form.short_name.trim(),
      code_prefix: form.code_prefix.trim() === "" ? null : form.code_prefix.trim(),
      sort_id:    form.sort_id.trim(),
    };

    try {
      if (editDepot) {
        const res = await axios.put(`/admin/depots/${editDepot.id}`, payload);
        setDepots((prev) => prev.map((d) => (d.id === editDepot.id ? { ...d, ...res.data } : d)));
        showFlash("更新しました");
      } else {
        const res = await axios.post("/admin/depots", payload);
        setDepots((prev) => [...prev, res.data]);
        showFlash("追加しました");
      }
      closeModal();
    } catch (e) {
      const errors = e.response?.data?.errors;
      if (errors) {
        showFlash(Object.values(errors).flat()[0], "error");
      } else {
        showFlash(e.response?.data?.message ?? "保存に失敗しました", "error");
      }
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──
  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "営業所管理" }]} />

      <Flash message={flash.message} type={flash.type} onClose={() => setFlash({ ...flash, message: "" })} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">営業所管理</h2>
          <span className="adm-badge adm-badge--violet">admin</span>
        </div>
        <button className="adm-btn adm-btn--primary" onClick={openAdd}>
          ＋ 営業所を追加
        </button>
      </div>

      {/* フィルター */}
      <div className="adm-filter-box">
        <div className="adm-filter-row">
          <div className="adm-filter-field">
            <span className="adm-filter-label">検索</span>
            <input
              type="text"
              className="adm-input"
              placeholder="営業所名・略称"
              value={searchQ}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ minWidth: 180 }}
            />
          </div>
        </div>
      </div>

      {/* テーブル */}
      <div className="adm-card">
        <div className="adm-card__header">
          営業所一覧
          <span className="adm-card__header-sub">{filtered.length} 件</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                {["ID", "営業所名", "略称", "乗番略号", "表示順", "車両数", "路線数", "登録日", "操作"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="adm-empty">読み込み中…</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={9} className="adm-empty">該当する営業所がありません</td></tr>
              ) : (
                pageItems.map((depot) => (
                  <tr key={depot.id}>
                    <td className="adm-table__id">{depot.id}</td>
                    <td className="adm-table__bold">{depot.name}</td>
                    <td>
                      <span className="adm-badge adm-badge--blue">{depot.short_name}</span>
                    </td>
                    <td className="adm-center adm-table__mute">{depot.code_prefix ?? "—"}</td>
                    <td className="adm-center adm-table__mute">{depot.sort_id}</td>
                    <td className="adm-center adm-table__mute">{depot.buses_count ?? 0}</td>
                    <td className="adm-center adm-table__mute">{depot.routes_count ?? 0}</td>
                    <td className="adm-table__mute">
                      {depot.created_at ? String(depot.created_at).slice(0, 10) : ""}
                    </td>
                    <td>
                      <button
                        className="adm-btn adm-btn--default adm-btn--sm"
                        onClick={() => openEdit(depot)}
                      >
                        編集
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          current={safePage}
          total={totalPages}
          onChange={setCurrentPage}
          count={filtered.length}
        />
      </div>

      {/* 追加・編集モーダル */}
      <Modal show={modalOpen} title={editDepot ? "営業所を編集" : "営業所を追加"} onClose={closeModal}>
        <FormField label="営業所名" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="例: 博多営業所"
            maxLength={255}
          />
        </FormField>
        <FormField label="略称" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.short_name}
            onChange={(e) => setForm((f) => ({ ...f, short_name: e.target.value }))}
            placeholder="例: 博多"
            maxLength={255}
          />
          <p style={{ fontSize: ".75rem", color: "var(--adm-text-muted)", margin: ".4rem 0 0" }}>
            ※ URLの識別子に使われます。他の営業所と重複しない値にしてください。
          </p>
        </FormField>
        <FormField label="乗番略号">
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.code_prefix}
            onChange={(e) => setForm((f) => ({ ...f, code_prefix: e.target.value }))}
            placeholder="例: ア"
            maxLength={8}
          />
          <p style={{ fontSize: ".75rem", color: "var(--adm-text-muted)", margin: ".4rem 0 0" }}>
            ※ 運用解析の乗番記号に使います（「ア1」の「ア」）。
            空欄なら略称の頭文字。頭文字が他とぶつかる営業所は、
            読み分けられる略号を入れてください（例: 福岡空港 → 空港）。
          </p>
        </FormField>
        <FormField label="表示順" required>
          <div className="adm-sort-row">
            <button
              type="button"
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => setForm((f) => ({ ...f, sort_id: String(Math.max(0, Number(f.sort_id || 0) - 1)) }))}
            >−1</button>
            <input
              type="number"
              className="adm-input adm-input--full"
              value={form.sort_id}
              onChange={(e) => setForm((f) => ({ ...f, sort_id: e.target.value }))}
              min={0} max={99999}
              placeholder="例: 1"
              style={{ textAlign: "center" }}
            />
            <button
              type="button"
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => setForm((f) => ({ ...f, sort_id: String(Math.min(99999, Number(f.sort_id || 0) + 1)) }))}
            >＋1</button>
          </div>
        </FormField>
        <ModalActions
          onCancel={closeModal}
          onConfirm={handleSave}
          confirmLabel={saving ? "保存中…" : "保存"}
          disabled={saving}
        />
      </Modal>
    </div>
  );
}
